import type { RegisteredSkill } from '../types'
import {
  runGetCurrentDocument,
  runGetDocumentOutline,
  runGetDocumentParagraphs,
  runGetFullDocumentComments,
  runGetSection,
  runGetSelectedParagraphs,
} from './handlers/read-handlers'

const paragraphIdTextListResultSchema = {
  type: 'array',
  items: {
    type: 'object',
    properties: {
      id: { type: 'string' },
      text: { type: 'string' },
    },
    required: ['id', 'text'],
  },
} as const

export const READ_SKILLS: RegisteredSkill[] = [
  {
    name: 'get_document_outline',
    description: 'Section tree (ids, titles, types). Call first when the user names a section.',
    argsSchema: {
      type: 'object',
      properties: {},
      additionalProperties: false,
    },
    resultSchema: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          title: { type: 'string' },
          type: { type: 'string' },
        },
        required: ['id', 'title', 'type'],
      },
    },
    handler: runGetDocumentOutline,
  },
  {
    name: 'get_current_document',
    description:
      'Plain text of the entire current document. Use when the request spans the whole document or context from specific sections is not enough for adequate response.',
    argsSchema: {
      type: 'object',
      properties: {},
      additionalProperties: false,
    },
    resultSchema: {
      type: 'string',
      description: 'Plain text of the entire current document',
    },
    handler: runGetCurrentDocument,
  },
  {
    name: 'get_section',
    description:
      'Content for one section by id (heading, synthetic __text body, or table). Prefer use_html false and per_paragraph true (plain text) unless formatting or table structure matters; use HTML when style must be preserved. With per_paragraph true, prose returns one { type, id, content } entry per paragraph; table cells are not returned individually — they are replaced by table_html entries (cell ids as <p id> in the HTML).',
    argsSchema: {
      type: 'object',
      properties: {
        id: { type: 'string', description: 'Section id from get_document_outline or section_ids' },
        use_html: {
          type: 'boolean',
          description:
            'Return HTML when true; plain text when false. Ignored for per_paragraph on headings/text.',
        },
        per_paragraph: {
          type: 'boolean',
          description:
            'When true, return one section entry per prose paragraph (plain text); table cells are merged into table_html entries instead of per-cell section entries.',
        },
      },
      required: ['id'],
      additionalProperties: false,
    },
    resultSchema: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          type: { type: 'string' },
          id: { type: 'string' },
          content: { type: 'string' },
        },
        required: ['type', 'content'],
      },
      description: 'Section entries (and optionally table_html) in the same shape as chat context',
    },
    handler: runGetSection,
  },
  {
    name: 'get_selected_paragraphs',
    description:
      'Paragraphs intersecting the current mouse selection as { id, text }. Empty when the cursor is collapsed (nothing selected). Use before insert_comments when scoping to the selection; pass the returned ids as selection_paragraph_ids. Uses the live selection at call time.',
    argsSchema: {
      type: 'object',
      properties: {},
      additionalProperties: false,
    },
    resultSchema: paragraphIdTextListResultSchema,
    handler: runGetSelectedParagraphs,
  },
  {
    name: 'get_document_paragraphs',
    description:
      'All body paragraphs as { id, text } for document-wide comment anchoring. Prefer over get_current_document when you need paragraph ids for insert_comments.',
    argsSchema: {
      type: 'object',
      properties: {},
      additionalProperties: false,
    },
    resultSchema: paragraphIdTextListResultSchema,
    handler: runGetDocumentParagraphs,
  },
  {
    name: 'get_full_document_comments',
    description:
      'Every comment in the document with comment_id, paragraph_id, search_text, comment_text, and optional author/resolved metadata. Use before add_comment_replies.',
    argsSchema: {
      type: 'object',
      properties: {},
      additionalProperties: false,
    },
    resultSchema: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          comment_id: { type: 'string' },
          comment_text: { type: 'string' },
          paragraph_id: { type: 'string' },
          search_text: { type: ['string', 'null'] },
          author_email: { type: 'string' },
          author_name: { type: 'string' },
          creation_date: { type: 'string' },
          resolved: { type: 'boolean' },
        },
        required: ['comment_id', 'comment_text'],
      },
    },
    handler: runGetFullDocumentComments,
  },
]
